import { useState, useMemo } from 'react';
import { isSafeUrl } from '../utils/url';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Search,
  RefreshCw,
  Filter,
  Clock,
  AlertCircle,
  UserPlus,
  ShieldCheck,
  CreditCard,
  Calendar,
  X,
} from 'lucide-react';
import { useNotificationContext } from '../contexts/NotificationContext';
import { Notification } from '../api/notifications';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { getNotificationColor, getNotificationIcon, getNotificationTypeLabel } from '@/helpers/common';

interface NotificationsProps {
  onNavigate?: (route: string) => void;
}
export function Notifications({ onNavigate }: NotificationsProps) {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
  } = useNotificationContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<Set<number>>(new Set());

  const notificationTypes = useMemo(() => {
    return Array.from(new Set(notifications.map((n) => n.type).filter(Boolean)));
  }, [notifications]);

  const hasActiveFilters = typeFilter !== 'all' || statusFilter !== 'all';

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const matchesSearch =
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (notification.description &&
          notification.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType = typeFilter === 'all' || notification.type === typeFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'read' && notification.read) ||
        (statusFilter === 'unread' && !notification.read);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [notifications, searchTerm, typeFilter, statusFilter]);

  const handleSelectAll = () => {
    if (selectedNotifications.size === filteredNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(filteredNotifications.map((n) => n.id)));
    }
  };

  const handleSelectNotification = (id: number) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNotifications(newSelected);
  };

  const handleMarkSelectedAsRead = async () => {
    const promises = Array.from(selectedNotifications)
      .filter((id) => !notifications.find((n) => n.id === id)?.read)
      .map((id) => markAsRead(id));

    await Promise.all(promises);
    setSelectedNotifications(new Set());
    toast.success('Selected notifications marked as read');
  };

  const handleDeleteSelected = async () => {
    const promises = Array.from(selectedNotifications).map((id) => deleteNotification(id));

    await Promise.all(promises);
    setSelectedNotifications(new Set());
    toast.success('Selected notifications deleted');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    toast.success('All notifications marked as read');
  };

  const handleClearAll = async () => {
    const promises = notifications.map((n) => deleteNotification(n.id));
    await Promise.all(promises);
    setSelectedNotifications(new Set());
    toast.success('All notifications cleared');
  };

  const handleRefresh = async () => {
    await refreshNotifications();
    toast.success('Notifications refreshed');
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.url && isSafeUrl(notification.url)) {
      window.location.href = notification.url;
    }
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
      <div className="flex flex-col gap-2">

        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
           <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
              <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">

                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground flex-shrink-0 z-10" />
                <Input
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-white rounded-lg shadow-sm border-border text-sm"
                  maxLength={200}
                />
              </div>
              <div className="relative w-full flex xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`flex-shrink-0 ${hasActiveFilters ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''}`}
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      <span>Filters</span>
                      {hasActiveFilters && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {[typeFilter, statusFilter].filter((f) => f !== 'all').length}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuLabel>Filter Notifications</DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    <div className="px-2 py-1">
                      <div className="text-sm font-medium text-muted-foreground mb-2">Status</div>
                      <div className="grid grid-cols-1 gap-1">
                        {[
                          { value: 'all', label: 'All' },
                          { value: 'unread', label: 'Unread', count: unreadCount },
                          { value: 'read', label: 'Read', count: notifications.length - unreadCount },
                        ].map((status) => (
                          <DropdownMenuItem
                            key={status.value}
                            onClick={() => setStatusFilter(status.value)}
                            className={`text-sm justify-between ${statusFilter === status.value
                                ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                : ''
                              }`}
                          >
                            <span>{status.label}</span>
                            {statusFilter === status.value && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </div>
                    </div>

                    <DropdownMenuSeparator />

                    <div className="px-2 py-1">
                      <div className="text-sm font-medium text-muted-foreground mb-2">Type</div>
                      <div className="grid grid-cols-1 gap-1">
                        <DropdownMenuItem
                          onClick={() => setTypeFilter('all')}
                          className={`text-sm justify-between ${typeFilter === 'all'
                              ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                              : ''
                            }`}
                        >
                          <span>All Types</span>
                          {typeFilter === 'all' && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                          )}
                        </DropdownMenuItem>
                        {notificationTypes.map((type) => (
                          <DropdownMenuItem
                            key={type}
                            onClick={() => setTypeFilter(type)}
                            className={`text-sm justify-between ${typeFilter === type
                                ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                : ''
                              }`}
                          >
                            <span>{getNotificationTypeLabel(type)}</span>
                            {typeFilter === type && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </div>
                    </div>

                    {(typeFilter !== 'all' || statusFilter !== 'all') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setTypeFilter('all');
                            setStatusFilter('all');
                          }}
                          className="text-sm text-destructive focus:text-destructive"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear all filters
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu></div>
            </div>


          </div>

           <div className="flex flex-col md:flex-row md:items-center gap-4 xl:shrink-0">

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {selectedNotifications.size > 0 && (
                <div className="flex items-center justify-between border-border">

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkSelectedAsRead}
                      className="shadow-sm"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark as read
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteSelected}
                      className="shadow-sm text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}


              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="shadow-sm"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark all read
                </Button>
              )}

              {notifications.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="shadow-sm text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear all
                </Button>
              )}
            </div>
          </div>
        </div>

      </div>



      {error && (
        <Card className="mb-6 border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-destructive mr-2" />
              <p className="text-sm text-destructive-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}


      {loading && notifications.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading notifications...</p>
          </CardContent>
        </Card>
      ) : filteredNotifications.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">No notifications found</p>
            
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              checked={selectedNotifications.size === filteredNotifications.length}
              onChange={handleSelectAll}
              className="w-4 h-4 rounded border-border"
            />
            <span className="text-sm text-muted-foreground">Select all</span>
          </div>

          {filteredNotifications.map((notification) => (
            <Card
              key={notification.id}
              className={`shadow-sm transition-all hover:shadow-md cursor-pointer ${!notification.read
                ? 'border-l-4 border-l-primary bg-accent/30'
                : 'bg-background'
                } ${selectedNotifications.has(notification.id)
                  ? 'ring-2 ring-primary ring-offset-2'
                  : ''
                }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-4">
                  <div className="flex items-start space-x-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.has(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 mt-1 rounded border-border"
                    />

                    <div
                      className={`mt-0.5 p-2 rounded-lg ${getNotificationColor(
                        notification.type
                      )} flex-shrink-0`}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center space-x-2 flex-1">
                          <h3
                            className={`text-sm font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'
                              }`}
                          >
                            {notification.message}
                          </h3>
                          {!notification.read && (
                            <Badge variant="default" className="flex-shrink-0">
                              New
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 hover:bg-primary/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              title="Mark as read"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            title="Delete notification"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {notification.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {notification.description}
                        </p>
                      )}

                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(notification.createdon), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>


                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
